// File Location: src/services/universalSearchService.js

import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from './firebaseConfig';

/**
 * Universal search service for searching applications across all user types
 * Searches by application ID or patient name
 */
export const searchApplicationsUniversal = async (searchTerm, userRole = null) => {
  try {
    if (!searchTerm || searchTerm.trim().length < 2) {
      return {
        success: true,
        results: [],
        message: 'Please enter at least 2 characters to search'
      };
    }

    const searchLower = searchTerm.trim().toLowerCase();
    const searchResults = [];

    // Base query - get all applications
    let q = collection(db, 'patientApplications');
    
    // If we have a role-based filter, apply it
    if (userRole === 'hospital') {
      // For now, we'll fetch all and filter on client side
      // In production, you might want role-based filtering
    }

    q = query(q, orderBy('submittedAt', 'desc'), limit(500)); // Limit to prevent performance issues

    const querySnapshot = await getDocs(q);

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Check if search term matches application ID
      const applicationIdMatch = data.applicationId?.toLowerCase().includes(searchLower);
      
      // Check if search term matches patient name
      const patientNameMatch = data.patient?.name?.toLowerCase().includes(searchLower);
      
      // Check if search term matches case ID
      const caseIdMatch = data.caseId?.toLowerCase().includes(searchLower);
      
      // Check if search term matches policy number
      const policyNumberMatch = data.policy?.number?.toLowerCase().includes(searchLower);
      
      // Check if search term matches hospital name
      const hospitalNameMatch = data.hospitalName?.toLowerCase().includes(searchLower);

      if (applicationIdMatch || patientNameMatch || caseIdMatch || policyNumberMatch || hospitalNameMatch) {
        searchResults.push({
          id: doc.id,
          applicationId: data.applicationId,
          caseId: data.caseId,
          patientName: data.patient?.name || 'N/A',
          hospitalName: data.hospitalName || 'N/A',
          status: data.status,
          submittedAt: data.submittedAt,
          priority: data.priority,
          emergency: data.emergency,
          insuranceCompany: data.policy?.insuranceCompany || 'N/A',
          policyNumber: data.policy?.number || 'N/A',
          diagnosis: data.medical?.diagnosis || 'N/A',
          // Add more fields as needed
          ...data
        });
      }
    });

    // Sort by relevance (exact matches first)
    const sortedResults = searchResults.sort((a, b) => {
      const aExactMatch = 
        a.applicationId?.toLowerCase() === searchLower ||
        a.caseId?.toLowerCase() === searchLower ||
        a.patientName?.toLowerCase() === searchLower;
      
      const bExactMatch = 
        b.applicationId?.toLowerCase() === searchLower ||
        b.caseId?.toLowerCase() === searchLower ||
        b.patientName?.toLowerCase() === searchLower;
      
      if (aExactMatch && !bExactMatch) return -1;
      if (!aExactMatch && bExactMatch) return 1;
      return 0;
    });

    return {
      success: true,
      results: sortedResults.slice(0, 20), // Limit to top 20 results
      total: sortedResults.length,
      message: sortedResults.length === 0 ? 'No applications found' : `${sortedResults.length} results found`
    };
  } catch (error) {
    console.error('Error searching applications:', error);
    return {
      success: false,
      results: [],
      error: error.message || 'Failed to search applications',
      total: 0
    };
  }
};

/**
 * Get application details for a specific application ID
 */
export const getApplicationById = async (applicationId) => {
  try {
    const q = query(
      collection(db, 'patientApplications'),
      where('applicationId', '==', applicationId)
    );

    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return {
        success: false,
        error: 'Application not found'
      };
    }

    const doc = querySnapshot.docs[0];
    return {
      success: true,
      application: {
        id: doc.id,
        ...doc.data()
      }
    };
  } catch (error) {
    console.error('Error getting application by ID:', error);
    return {
      success: false,
      error: error.message || 'Failed to get application'
    };
  }
};

